import { Injectable, computed, inject } from '@angular/core';
import { forkJoin } from 'rxjs';
import { MenuGroup, MenuItem } from '../../layout/app-shell/models/menu.model';
import { PermissionService } from './permission.service';
import { ModuleService } from './module.service';

/**
 * Segmentos de rota REALMENTE implementados (existem em app.routes.ts →
 * tenantFeatureRoutes e não são placeholder). Fonte da verdade: leitura de
 * src/app/app.routes.ts em 2026-07 — atualizar sempre que uma feature sair
 * do estágio placeholder ou uma nova rota real for adicionada.
 */
const AVAILABLE_ROUTES = new Set<string>([
  'dashboard',
  'cadastros/pessoas',
  'projetos',
  'coletas',
  'coletas/certificados',
  'coletas/config',
  'coletas/operacao',
  'coletas/operacao/mapa',
  'financeiro',
  'financeiro/contas-pagar',
  'financeiro/contas-receber',
  'financeiro/cadastros',
  'financeiro/cadastros/contas-bancarias',
  'financeiro/cadastros/categorias',
  'financeiro/parametros',
  'financeiro/fechamento-agenda',
  'financeiro/fechamento-comissao',
  'tickets',
  'tickets/config-sla',
  'cilindros',
  'cilindros/monitor',
  'cilindros/mapa',
  'cilindros/configuracoes',
  'configuracoes',
  'estoque',
  'estoque/depositos',
  'estoque/configuracoes',
  'estoque/saldo',
  'crm/funil',
  'crm/config/estagios',
  'crm/config/motivos',
]);

/**
 * Rotas que existem em app.routes.ts mas cujo componente ainda é apenas um
 * placeholder de tela (sem funcionalidade real implementada). Tratadas como
 * "inexistentes" para fins de menu até a feature real ser entregue — não
 * remover daqui só porque a rota responde; remover quando a TELA for real.
 *
 * 'estoque' saiu daqui em 2026-07 — Estoque v1 retaguarda parte 1 (depósitos,
 * movimentos, transferência, ledger de produto e configurações) entregue.
 * Nota corrigida (ADR-012, front#149): a Tela 1 (saldo consolidado,
 * `estoque/saldo`) já consome `GET /api/estoque/saldos` — endpoint em lote
 * (api#352) implementado — não é mais placeholder, é rota real.
 *
 * 'crm' saiu daqui em 2026-07 — CRM v1 (funil de oportunidades, detalhe e
 * configurações) entregue (issue frontend #122, contrato api#351).
 */
const PLACEHOLDER_ROUTES = new Set<string>(['certificados', 'fiscal', 'servicos', 'relatorios']);

/**
 * Catálogo estático de itens de menu. NÃO é a fonte de visibilidade — é
 * apenas a lista de candidatos. MenuService.groups() filtra por rota
 * existente + módulo habilitado + permissão antes de expor ao Sidebar.
 *
 * requiredModule (ARC-064): o backend (`api.compleet.com.br` —
 * `Persistense/Admin/DbInitializer.cs`) só tem 5 acronyms reais de módulo
 * Compleet cadastrados: `CMPADM` (Administração), `CMPVEN` (Vendas),
 * `CMPFIN` (Financeiro), `CMPCIL` (Cilindros), `CMPLOG` (Logística).
 * Preenchidos apenas onde a correspondência item↔acronym é inequívoca:
 * - `cilindros` → `CMPCIL` (bug relatado no ARC-064: item aparecia sob
 *   empresa sem o módulo contratado).
 * - `financeiro` → `CMPFIN` (nome idêntico ao módulo).
 * - `crm` → `CMPVEN` (CRM é o domínio "Vendas" no Compleet — ver CLAUDE.md,
 *   seção "CRM e Vendas").
 * - `coletas` e `coletas-config` → `CMPLOG` (ARC-069/ARC-072, front#163/#169:
 *   decisão do PO 2026-07-31 — Coletas é coberto pelo módulo Logística, sem
 *   acronym próprio; os dois itens do módulo (Operação e retaguarda de
 *   Configurações) recebem o mesmo gate por consistência).
 * Os demais itens (Entregas, Projetos, Estoque, Tickets, Contratos,
 * Produtos, Veículos, Usuários, Relatórios, Integrações, Parâmetros,
 * Permissões etc.) permanecem SEM requiredModule: não há acronym backend
 * com correspondência inequívoca para eles — mapear por suposição
 * arriscaria ocultar telas funcionando para tenants sem o acronym
 * "adivinhado". Reavaliar quando o backend confirmar o mapeamento
 * completo (abrir issue em api.compleet.com.br se necessário).
 * requiredPermission fica vazio até o catálogo de permissões PBAC ser
 * confirmado pelo backend (ver docs/seguranca-pbac-frontend-mobile.md) —
 * primeira exceção confirmada: `cilindros-mapa` → `CMPCIL.geral.visualizar`
 * (ARC-084/front#171, contrato publicado em api#386). Segunda exceção:
 * `cilindros-config` (grupo `configuracoes` desde 2026-10-13, front#192 —
 * nasceu como `cilindros-configuracoes` sob o item Cilindros) →
 * `CMPCIL.geral.editar` (ARC-084/front#175, contrato publicado em
 * api#401/#402 — tela de escrita, catálogo só semeia `{Modulo}.geral.{acao}`,
 * sem namespace próprio de config).
 */
const CATALOG: MenuGroup[] = [
  {
    id: 'inicio',
    label: '',
    items: [{ id: 'dashboard', label: 'Dashboard', icon: 'home', routeSegment: 'dashboard' }],
  },
  {
    id: 'operacao',
    label: 'Operação',
    items: [
      // ARC-060 C4: a operação de coletas (lista/mapa em tempo real) vira a tela
      // primária do item "Coletas" — a retaguarda de configuração por
      // cliente/certificados (rota 'coletas') continua acessível via o item
      // "Configurações de coleta" no grupo Configurações, não some do menu.
      // ARC-069 (front#163): requiredModule 'CMPLOG' — decisão do PO
      // (2026-07-31): Coletas é coberto pelo módulo Logística, sem acronym
      // próprio. Fecha a lacuna do ARC-064 (item ficava sem requiredModule
      // por falta de acronym confirmado).
      { id: 'coletas', label: 'Coletas', icon: 'droplet', routeSegment: 'coletas/operacao', requiredModule: 'CMPLOG' },
      { id: 'entregas', label: 'Entregas', icon: 'truck', routeSegment: 'entregas' },
      { id: 'projetos', label: 'Projetos', icon: 'folder', routeSegment: 'projetos' },
      { id: 'servicos', label: 'Ordens de Serviço', icon: 'wrench', routeSegment: 'servicos' },
      {
        // ADR-012: "Cilindros" deixou de ser 1 rota com abas (Monitor/
        // Configurações eram funcionalidades distintas) — cada uma virou
        // rota própria. Monitor e Mapa da carga ficam aqui como submenu;
        // Configurações mudou de grupo em 2026-10-13 (ver 'cilindros-config'
        // em 'configuracoes').
        id: 'cilindros',
        label: 'Cilindros',
        icon: 'cylinder',
        routeSegment: 'cilindros',
        requiredModule: 'CMPCIL',
        children: [
          { id: 'cilindros-monitor', label: 'Monitor', icon: 'cylinder', routeSegment: 'cilindros/monitor' },
          // Fase B (ARC-084/front#171): gate PBAC confirmado pelo backend em
          // api#386 — 'CMPCIL.geral.visualizar' (mesmo gate do hub
          // /hubs/cilindros-veiculo). Primeiro item do catálogo com
          // requiredPermission — ver menu.service.spec.ts.
          {
            id: 'cilindros-mapa',
            label: 'Mapa da carga',
            icon: 'map',
            routeSegment: 'cilindros/mapa',
            requiredPermission: 'CMPCIL.geral.visualizar',
          },
          // A configuração de rastreamento saiu daqui (decisão do PO
          // 2026-10-13, front#192) — mora em 'configuracoes' como
          // 'cilindros-config', no padrão de 'coletas-config'.
        ],
      },
    ],
  },
  {
    id: 'cadastros',
    label: 'Cadastros',
    items: [
      // "Clientes" → "Pessoas" (ADR-008): Cliente/Fornecedor/… são papéis dentro de Pessoas.
      { id: 'pessoas', label: 'Pessoas', icon: 'contact', routeSegment: 'cadastros/pessoas' },
      { id: 'produtos', label: 'Produtos', icon: 'tag', routeSegment: 'produtos' },
      { id: 'veiculos', label: 'Veículos', icon: 'truck', routeSegment: 'veiculos' },
      { id: 'usuarios', label: 'Usuários', icon: 'users', routeSegment: 'usuarios' },
    ],
  },
  {
    id: 'gestao',
    label: 'Gestão',
    items: [
      {
        // ADR-012: "Estoque" deixou de ser 1 rota com abas (Depósitos/
        // Configurações/Saldo de produtos eram funcionalidades distintas)
        // — cada uma virou rota própria, exposta aqui como submenu.
        id: 'estoque',
        label: 'Estoque',
        icon: 'warehouse',
        routeSegment: 'estoque',
        children: [
          { id: 'estoque-depositos', label: 'Depósitos', icon: 'warehouse', routeSegment: 'estoque/depositos' },
          { id: 'estoque-configuracoes', label: 'Configurações', icon: 'settings', routeSegment: 'estoque/configuracoes' },
          { id: 'estoque-saldo', label: 'Saldo de produtos', icon: 'chart', routeSegment: 'estoque/saldo' },
        ],
      },
      { id: 'ativo-imobilizado', label: 'Ativo Imobilizado', icon: 'box', routeSegment: 'ativo-imobilizado' },
      {
        // ADR-012 (cadeia Sidebar → Módulo → Submenu → Página): Contas a
        // receber, Cadastros, Parâmetros e Fechamento (agenda/comissão) já
        // eram rotas reais em app.routes.ts mas não tinham item de menu —
        // inacessíveis pela UI (só por URL direta). Agora expostos como
        // submenu do módulo Financeiro. requiredModule (ARC-064): gate do
        // grupo inteiro — children só são avaliados se o módulo estiver
        // habilitado (ver resolveItem).
        id: 'financeiro',
        label: 'Financeiro',
        icon: 'currency',
        routeSegment: 'financeiro',
        requiredModule: 'CMPFIN',
        children: [
          { id: 'financeiro-contas-pagar', label: 'Contas a pagar', icon: 'currency', routeSegment: 'financeiro/contas-pagar' },
          { id: 'financeiro-contas-receber', label: 'Contas a receber', icon: 'currency', routeSegment: 'financeiro/contas-receber' },
          {
            // ADR-012 (front#150): "Cadastros" deixou de ser 1 rota com
            // abas (Contas bancárias/Categoria) — cada uma virou rota
            // própria. MenuItem.children não tem netos (só 2 níveis), então
            // as duas entram direto no submenu do Financeiro, não aninhadas
            // sob um "Cadastros" intermediário.
            id: 'financeiro-cadastros-contas-bancarias',
            label: 'Cadastros: Contas bancárias',
            icon: 'tag',
            routeSegment: 'financeiro/cadastros/contas-bancarias',
          },
          {
            id: 'financeiro-cadastros-categorias',
            label: 'Cadastros: Categorias',
            icon: 'tag',
            routeSegment: 'financeiro/cadastros/categorias',
          },
          { id: 'financeiro-parametros', label: 'Parâmetros', icon: 'settings', routeSegment: 'financeiro/parametros' },
          { id: 'financeiro-fechamento-agenda', label: 'Fechamento de agenda', icon: 'chart', routeSegment: 'financeiro/fechamento-agenda' },
          { id: 'financeiro-fechamento-comissao', label: 'Fechamento de comissão', icon: 'chart', routeSegment: 'financeiro/fechamento-comissao' },
        ],
      },
      {
        // ADR-012: "Configurações" do CRM deixou de ser 1 rota com abas
        // (Estágios/Motivos eram funcionalidades distintas) — cada uma
        // virou rota própria, exposta aqui como submenu. requiredModule
        // (ARC-064): gate do grupo inteiro, mesma regra do Financeiro acima.
        id: 'crm',
        label: 'CRM',
        icon: 'group',
        routeSegment: 'crm/funil',
        requiredModule: 'CMPVEN',
        children: [
          { id: 'crm-funil', label: 'Funil de oportunidades', icon: 'group', routeSegment: 'crm/funil' },
          { id: 'crm-config-estagios', label: 'Estágios do funil', icon: 'settings', routeSegment: 'crm/config/estagios' },
          { id: 'crm-config-motivos', label: 'Motivos de perda', icon: 'settings', routeSegment: 'crm/config/motivos' },
        ],
      },
      { id: 'contratos', label: 'Contratos', icon: 'file', routeSegment: 'contratos' },
      {
        // ADR-012: "Configurações de SLA" deixou de ser 1 rota com abas —
        // virou rota própria, exposta aqui como submenu.
        id: 'tickets',
        label: 'Tickets',
        icon: 'ticket',
        routeSegment: 'tickets',
        children: [
          { id: 'tickets-lista', label: 'Tickets', icon: 'ticket', routeSegment: 'tickets' },
          { id: 'tickets-config-sla', label: 'Configurações de SLA', icon: 'settings', routeSegment: 'tickets/config-sla' },
        ],
      },
    ],
  },
  {
    id: 'relatorios',
    label: 'Relatórios',
    items: [
      { id: 'relatorios-operacionais', label: 'Operacionais', icon: 'chart', routeSegment: 'relatorios/operacionais' },
      { id: 'relatorios-financeiros', label: 'Financeiros', icon: 'chart', routeSegment: 'relatorios/financeiros' },
      { id: 'relatorios-bi', label: 'BI e Indicadores', icon: 'chart', routeSegment: 'relatorios/bi' },
    ],
  },
  {
    id: 'configuracoes',
    label: 'Configurações',
    items: [
      { id: 'integracoes', label: 'Integrações', icon: 'modules', routeSegment: 'integracoes' },
      { id: 'parametros', label: 'Parâmetros', icon: 'settings', routeSegment: 'parametros' },
      { id: 'permissoes', label: 'Permissões', icon: 'users', routeSegment: 'permissoes' },
      // ARC-060 C4: retaguarda antiga de coletas (config por cliente/certificados),
      // preservada aqui já que o item "Coletas" da Operação passou a apontar
      // para 'coletas/operacao' (mapa/lista em tempo real).
      // ADR-012 (front#151): "Configurações de coleta" deixou de ser 1 rota
      // com abas (Clientes/Certificados/Configurações eram funcionalidades
      // distintas) — cada uma virou rota própria, exposta aqui como submenu.
      // ADR-013/ARC-068 (front#161): o filho "Clientes" saiu daqui — a
      // config por cliente virou a aba condicional "Coleta" no cadastro
      // central de Pessoa (cadastro de Pessoa único e centralizado, sem
      // lista de pessoas por módulo). Restam Certificados e Configurações.
      // ARC-072 (front#169): requiredModule 'CMPLOG' por consistência com o
      // item "Coletas" (Operação, ARC-069) — mesmo módulo, mesmo gate.
      {
        id: 'coletas-config',
        label: 'Configurações de coleta',
        icon: 'settings',
        routeSegment: 'coletas',
        requiredModule: 'CMPLOG',
        children: [
          { id: 'coletas-config-certificados', label: 'Certificados', icon: 'file', routeSegment: 'coletas/certificados' },
          { id: 'coletas-config-config', label: 'Configurações', icon: 'settings', routeSegment: 'coletas/config' },
        ],
      },
      // Decisão do PO (2026-10-13, front#192): a configuração de rastreamento
      // de cilindros (ARC-084/front#175) saiu do submenu de Cilindros e entra
      // aqui, no padrão de 'coletas-config' — mesma rota e mesmos gates
      // (módulo + PBAC de escrita), só muda a posição no menu.
      {
        id: 'cilindros-config',
        label: 'Configurações de cilindros',
        icon: 'settings',
        routeSegment: 'cilindros/configuracoes',
        requiredModule: 'CMPCIL',
        requiredPermission: 'CMPCIL.geral.editar',
      },
      { id: 'configuracoes', label: 'Configurações', icon: 'settings', routeSegment: 'configuracoes' },
    ],
  },
];

/**
 * Deriva o menu lateral visível a partir do catálogo estático, filtrando
 * por rota existente (e não-placeholder), módulo habilitado no tenant e
 * permissão do usuário. Nunca decidir visibilidade de item no template —
 * essa é a única fonte de verdade (regra central do App Shell).
 */
@Injectable({ providedIn: 'root' })
export class MenuService {
  private readonly permissionService = inject(PermissionService);
  private readonly moduleService = inject(ModuleService);

  readonly groups = computed<MenuGroup[]>(() =>
    CATALOG.map(group => ({
      ...group,
      items: group.items
        .map(item => this.resolveItem(item))
        .filter((item): item is MenuItem => item !== null),
    })).filter(group => group.items.length > 0)
  );

  /** Recarrega permissões e módulos — chamar no login e a cada troca de tenant. */
  reload() {
    return forkJoin([this.permissionService.load(), this.moduleService.load()]);
  }

  /**
   * Resolve um item (e filhos, até 2 níveis) para o menu (ARC-063):
   * - **Oculto** (retorna null) quando o módulo não é contratado OU falta
   *   permissão — "sem acesso" não aparece.
   * - **Ativo** quando a tela existe (rota real): navega normalmente.
   * - **Em breve** (`comingSoon: true`) quando o módulo/permissão liberam mas a
   *   tela ainda NÃO foi construída (rota inexistente/placeholder): aparece
   *   desabilitado, não navega. "Em breve" ≠ "sem acesso".
   * Agrupador (com children) aparece só com ≥1 filho resolvido.
   */
  private resolveItem(item: MenuItem): MenuItem | null {
    if (item.requiredModule && !this.moduleService.isEnabled(item.requiredModule)) return null;
    if (item.requiredPermission && !this.permissionService.can(item.requiredPermission)) return null;

    if (item.children && item.children.length > 0) {
      const children = item.children
        .map(child => this.resolveItem(child))
        .filter((c): c is MenuItem => c !== null);
      if (children.length === 0) return null;
      return { ...item, children };
    }

    const telaPronta = !!item.routeSegment && this.routeExists(item.routeSegment);
    return { ...item, comingSoon: !telaPronta };
  }

  private routeExists(routeSegment: string): boolean {
    return AVAILABLE_ROUTES.has(routeSegment) && !PLACEHOLDER_ROUTES.has(routeSegment);
  }
}
